import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface OnboardingFrame {
  title: string;
  subtitle: string;
  bullets: string[];
  imagePath: string;
}

const ONBOARDING_FRAMES: OnboardingFrame[] = [
  {
    title: "Monitor the metrics that matter",
    subtitle: "Dashboards & reports that surface what's important",
    bullets: [
      "See GMV, content volume, creator activity at a glance or create your own dashboards",
      "Build and schedule reports in clicks, not hours",
      "Actionable recommendations tailored to your brand",
    ],
    imagePath: "/onboarding/step-1.png",
  },
  {
    title: "Find the right creators, faster",
    subtitle: "Deeper data. Better matches.",
    bullets: [
      "See unique data on creators' styles and behavior",
      "Pre-grouped by brand match, performance, and posting frequency",
      "Filter by audience demographics, followers, post rate and more",
    ],
    imagePath: "/onboarding/step-2.png",
  },
  {
    title: "Manage collaborations end-to-end",
    subtitle: "From outreach to delivery, all in one place",
    bullets: [
      "Track and manage every creator through collaboration workflows",
      "Move faster with improved visibility of creators' status in collaborations",
      "See all creator activity with your brand in a unified view",
    ],
    imagePath: "/onboarding/step-3.png",
  },
  {
    title: "Protect your brand reputation",
    subtitle: "Know who is representing you, and how",
    bullets: [
      "See which creators pose the highest risk",
      "Understand what brand guidelines are being broken regularly",
      "Take action to protect your brand before issues spread",
    ],
    imagePath: "/onboarding/step-4.png",
  },
  {
    title: "Give creators the insights to maximize revenue",
    subtitle: "Data-driven briefs, tailored to your brand",
    bullets: [
      "Generate briefs based on your highest performing content",
      "Share your briefs directly with your creators and track views",
      "Give creators key insights and brand guidelines to drive GMV",
    ],
    imagePath: "/onboarding/step-5.png",
  },
];

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
  isOnboardingComplete: boolean;
}

export function OnboardingModal({ open, onComplete, isOnboardingComplete }: OnboardingModalProps) {
  const [currentFrame, setCurrentFrame] = useState(0);

  // Reset to first frame when modal opens
  useEffect(() => {
    if (open) {
      setCurrentFrame(0);
    }
  }, [open]);

  // Preload all images when modal opens for instant navigation
  useEffect(() => {
    if (open) {
      ONBOARDING_FRAMES.forEach((frame) => {
        const img = new Image();
        img.src = frame.imagePath;
      });
    }
  }, [open]);

  const handleNext = () => {
    if (currentFrame < ONBOARDING_FRAMES.length - 1) {
      setCurrentFrame((prev) => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentFrame > 0) {
      setCurrentFrame((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    // On first-time view, Skip closes the modal (completes onboarding)
    // On Product Tour view, this button is hidden anyway
    if (!isOnboardingComplete) {
      onComplete();
    }
  };

  const handleGetStarted = () => {
    onComplete();
  };

  const frame = ONBOARDING_FRAMES[currentFrame];
  const isFirstFrame = currentFrame === 0;
  const isLastFrame = currentFrame === ONBOARDING_FRAMES.length - 1;

  return (
    <DialogPrimitive.Root
      open={open}
      modal
      onOpenChange={(isOpen) => {
        // Only allow closing via dialog interactions if onboarding is already complete
        if (!isOpen && isOnboardingComplete) {
          onComplete();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed h-[565px] left-[50%] top-[50%] z-50 grid w-full max-w-[800px] translate-x-[-50%] translate-y-[-50%] gap-0 p-0 shadow-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          onEscapeKeyDown={(e) => {
            // Allow ESC to close if onboarding is complete
            if (!isOnboardingComplete) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Allow click outside to close if onboarding is complete
            if (!isOnboardingComplete) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            // Allow interaction outside to close if onboarding is complete
            if (!isOnboardingComplete) {
              e.preventDefault();
            }
          }}
        >
          <div className="flex gap-0 h-[565px]">
            {/* Left Column - Content */}
            <div className="flex flex-col justify-between bg-white flex-1 rounded-l-xl overflow-hidden">
              <div className="p-10">
                <div className="flex flex-col gap-12">
                  <div className="flex flex-col gap-2">
                    <h2 className="text-3xl font-medium text-gray-900 leading-tight">{frame.title}</h2>
                    <p className="text-base text-gray-600">{frame.subtitle}</p>
                  </div>

                  <ul className="flex flex-col gap-3">
                    {frame.bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="text-lg mt-[-3.5px] text-gray-900 leading-relaxed">•</span>
                        <span className="text-base text-gray-700 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between p-6">
                <div className="flex gap-2 h-[38px]">
                  {!isFirstFrame && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Back
                    </button>
                  )}
                </div>
                <div className="flex gap-3.5">
                  {ONBOARDING_FRAMES.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "w-2 h-2 rounded-full transition-colors",
                        index === currentFrame ? "bg-[#281D31]" : "bg-gray-300",
                      )}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  {/* Pagination Dots */}

                  {isLastFrame ? (
                    <button
                      type="button"
                      onClick={handleGetStarted}
                      className="px-4 py-2 text-sm font-semibold text-white bg-[#281D31] rounded-lg hover:bg-[#241231]"
                    >
                      {isOnboardingComplete ? "Done" : "Get Started"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="px-4 py-2 text-sm font-semibold text-white bg-[#281D31] rounded-lg hover:bg-[#241231]"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative w-[390px] overflow-hidden rounded-r-xl">
              {/* Skip button: only show on first-time onboarding, not on Product Tour */}
              {!isLastFrame && !isOnboardingComplete && (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="absolute top-4 right-4 px-4 py-2 text-sm font-normal text-white hover:text-gray-400 transition-colors z-10"
                >
                  Skip
                </button>
              )}
              <img src={frame.imagePath} alt={frame.title} className="w-full h-full object-cover block" />
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
